import React, { createContext, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { translations, languageOptions } from '../i18n/translations';

const LanguageContext = createContext(null);

const LANGUAGE_COOKIE = 'silamezs_lang';
const LANGUAGE_STORAGE = 'silamezs_lang';
const LOCALE_MAP = {
  lv: 'lv-LV',
  en: 'en-GB',
  ru: 'ru-RU',
  lt: 'lt-LT',
  et: 'et-EE',
  sv: 'sv-SE',
  fi: 'fi-FI',
  no: 'nb-NO',
  da: 'da-DK'
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const writeCookie = (name, value, days = 365) => {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; SameSite=Lax`;
};

const readStoredLanguage = () => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE) || readCookie(LANGUAGE_COOKIE);
  if (stored && translations[stored]) {
    return stored;
  }
  return null;
};

const persistLanguage = (code) => {
  writeCookie(LANGUAGE_COOKIE, code);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LANGUAGE_STORAGE, code);
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = readStoredLanguage();
    if (!stored) {
      persistLanguage('lv');
    }
    return stored || 'lv';
  });
  const location = useLocation();

  const isClientPortalRoute = location.pathname.startsWith('/portal') && !location.pathname.startsWith('/portal/admin');
  const activeLanguage = isClientPortalRoute ? 'lv' : language;

  const content = useMemo(() => translations[activeLanguage] || translations.lv, [activeLanguage]);

  const readValue = (source, parts) => {
    let current = source;
    for (const part of parts) {
      if (current && Object.prototype.hasOwnProperty.call(current, part)) {
        current = current[part];
      } else {
        return undefined;
      }
    }
    return current;
  };

  const t = (path, fallback = '') => {
    const parts = path.split('.');
    const value = readValue(content, parts);
    if (value !== undefined) {
      return value;
    }
    const fallbackValue = readValue(translations.lv, parts);
    if (fallbackValue !== undefined) {
      return fallbackValue;
    }
    return fallback || path;
  };

  const changeLanguage = (code) => {
    if (!translations[code]) return;
    setLanguage(code);
    persistLanguage(code);
  };

  return (
    <LanguageContext.Provider value={{ language: activeLanguage, changeLanguage, t, content, languageOptions, locale: LOCALE_MAP[activeLanguage] || 'lv-LV' }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
